import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, Save, CheckCircle, AlertCircle, Mail, Lock, Bell } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

const CandidateSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState(null);
  const [candidateId, setCandidateId] = useState(null);
  const [userEmail, setUserEmail] = useState('');

  const [notificationSettings, setNotificationSettings] = useState({
    email_application_updates: true,
    email_new_opportunities: true,
    email_messages: true,
    email_weekly_digest: false,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        setError('Not authenticated');
        return;
      }

      setUserEmail(session.user.email);

      const { data: candidate, error: fetchError } = await supabase
        .from('candidates')
        .select('id')
        .eq('user_id', session.user.id)
        .single();

      if (fetchError) throw fetchError;
      if (candidate) setCandidateId(candidate.id);

      // Fetch notification preferences from API
      const API_URL = process.env.REACT_APP_API_URL || 'https://appsytec-backend.vercel.app/api';
      const response = await fetch(`${API_URL}/notifications/preferences`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.preferences) {
          const prefs = data.preferences;
          setNotificationSettings({
            email_application_updates: prefs.email_application_updates ?? true,
            email_new_opportunities: prefs.email_new_opportunities ?? true,
            email_messages: prefs.email_messages ?? true,
            email_weekly_digest: prefs.email_weekly_digest ?? false,
          });
        }
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationChange = (key) => {
    setNotificationSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    setError(null);
    setSuccess('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const API_URL = process.env.REACT_APP_API_URL || 'https://appsytec-backend.vercel.app/api';
      const response = await fetch(`${API_URL}/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(notificationSettings),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save preferences');
      }

      setSuccess('Notification preferences saved.');
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    setSendingReset(true);
    setError(null);
    setSuccess('');

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(userEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) throw resetError;

      setSuccess(`Password reset email sent to ${userEmail}. Please check your inbox.`);
    } catch (err) {
      console.error('Error sending password reset:', err);
      setError(err.message);
    } finally {
      setSendingReset(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground mt-2">
          Manage your account preferences and security
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 text-green-900 border-green-200">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Email Notifications */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            <CardTitle>Email Notifications</CardTitle>
          </div>
          <CardDescription>Choose which emails you'd like to receive</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <label className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-accent">
              <div className="flex-1">
                <p className="font-medium">Application Updates</p>
                <p className="text-sm text-muted-foreground">
                  Receive notifications when your application status changes
                </p>
              </div>
              <input
                type="checkbox"
                checked={notificationSettings.email_application_updates}
                onChange={() => handleNotificationChange('email_application_updates')}
                className="h-5 w-5 rounded border-gray-300"
              />
            </label>

            <label className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-accent">
              <div className="flex-1">
                <p className="font-medium">New Opportunities</p>
                <p className="text-sm text-muted-foreground">
                  Get notified about new job opportunities that match your profile
                </p>
              </div>
              <input
                type="checkbox"
                checked={notificationSettings.email_new_opportunities}
                onChange={() => handleNotificationChange('email_new_opportunities')}
                className="h-5 w-5 rounded border-gray-300"
              />
            </label>

            <label className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-accent">
              <div className="flex-1">
                <p className="font-medium">Messages</p>
                <p className="text-sm text-muted-foreground">
                  Receive emails when you get new messages from recruiters
                </p>
              </div>
              <input
                type="checkbox"
                checked={notificationSettings.email_messages}
                onChange={() => handleNotificationChange('email_messages')}
                className="h-5 w-5 rounded border-gray-300"
              />
            </label>

            <label className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-accent">
              <div className="flex-1">
                <p className="font-medium">Weekly Digest</p>
                <p className="text-sm text-muted-foreground">
                  Get a weekly summary of your activity and new opportunities
                </p>
              </div>
              <input
                type="checkbox"
                checked={notificationSettings.email_weekly_digest}
                onChange={() => handleNotificationChange('email_weekly_digest')}
                className="h-5 w-5 rounded border-gray-300"
              />
            </label>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={handleSaveNotifications} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Preferences
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Security */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            <CardTitle>Account Security</CardTitle>
          </div>
          <CardDescription>Manage your password and account security</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 border rounded-lg bg-accent/50">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-medium">Email Address</p>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{userEmail}</p>

            <div className="flex items-center gap-2 mb-3">
              <Lock className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-medium">Password</p>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Click the button below to receive a password reset link via email
            </p>

            <Button
              variant="outline"
              onClick={handlePasswordReset}
              disabled={sendingReset}
            >
              {sendingReset ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Password Reset Email'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Information */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
          <CardDescription>Your account details</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Account Type</span>
              <span className="font-medium">Candidate</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Email</span>
              <span className="font-medium">{userEmail}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Status</span>
              <span className="font-medium text-green-600">Active</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CandidateSettings;
